/**
 * zod schemas of the API payloads (docs/API.md). The Worker validates what it sends with these,
 * and the web app parses what it receives with them.
 */
import { z } from "zod";
import { MixInput, ScenarioInput } from "./jobs";
import { Address, DISCLAIMER, Hash32, PersonaMix, RunId, RunStatus, StopReason, Timestamp } from "./run";

export const RunSummary = z.strictObject({
  runId: RunId,
  title: z.string(),
  createdAt: Timestamp,
  publishedAt: Timestamp,
  status: RunStatus,
  stopReason: StopReason.nullable(),
  crowdSize: z.int().min(1),
  rounds: z.int().nonnegative(),
  personaMix: PersonaMix,
  models: z.strictObject({ agents: z.string(), report: z.string() }),
  hash: Hash32,
  featured: z.boolean(),
  market: z.strictObject({
    symbol: z.string(),
    priceStart: z.string(),
    priceEnd: z.string(),
    /** Display only: computed with floats from the decimal strings. */
    priceChangePct: z.number(),
    swaps: z.int().nonnegative(),
    posts: z.int().nonnegative(),
  }),
  /** priceStart, then each round's price; floats for drawing only. */
  sparkline: z.array(z.number()),
  turningPointRound: z.int().min(1).nullable(),
  /** report.summary, at most 280 characters. */
  summary: z.string(),
});

const Disclaimer = z.literal(DISCLAIMER);

export const RunListResponse = z.strictObject({
  runs: z.array(RunSummary),
  nextCursor: z.string().nullable(),
  disclaimer: Disclaimer,
});

export const FeaturedRunResponse = z.strictObject({
  run: RunSummary,
  disclaimer: Disclaimer,
});

export const ArtifactResponse = z.strictObject({
  url: z.url(),
  expiresAt: Timestamp,
});

export const PublishResponse = z.strictObject({
  runId: RunId,
  hash: Hash32,
  publishedAt: Timestamp,
});

export const ErrorResponse = z.strictObject({
  error: z.strictObject({ code: z.string(), message: z.string() }),
});

export const HealthResponse = z.strictObject({
  ok: z.literal(true),
  version: z.string(),
  /** What this API has switched on: a page can say so before asking for a wallet. */
  features: z.strictObject({ payments: z.boolean(), oracle: z.boolean(), holder: z.boolean() }),
  /** The largest run the API can quote; `maxDecisions` (agents x rounds) follows the daily LLM cap. */
  limits: z.strictObject({
    minAgents: z.int(),
    maxAgents: z.int(),
    minRounds: z.int(),
    maxRounds: z.int(),
    maxDecisions: z.int().nonnegative(),
  }),
});

/** A `RunSummary` as derived from `run.json`, before the API adds `publishedAt` and `featured`. */
export const DerivedRunSummary = RunSummary.omit({ publishedAt: true, featured: true });

/**
 * What the runner reports about a run it verified. On the Workers Free plan the Worker does not
 * parse or re-hash the run (docs/OPEN_ITEMS.md item 21), so these come from the runner.
 */
export const RunMeta = z.strictObject({
  hash: Hash32,
  scenarioHash: Hash32,
  /** SHA-256 of the exact bytes uploaded: lowercase hex, no `0x`. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  summary: DerivedRunSummary,
  usage: z.strictObject({ costUsd: z.number().nonnegative(), llmCalls: z.int().nonnegative() }),
  /** The round of each injected event. */
  eventRounds: z.array(z.int().min(1)),
});

/** The metadata line of `POST /internal/runs/publish`. */
export const PublishMeta = z.strictObject({
  hash: Hash32,
  sha256: RunMeta.shape.sha256,
  summary: DerivedRunSummary,
  /** What an oracle commit of this run needs. Without it the run is published and not committed. */
  scenarioHash: Hash32.optional(),
  featured: z.boolean().optional(),
});

// ---------------------------------------------------------------------------------------------
// Sign-in with Ethereum

export const NonceResponse = z.strictObject({
  nonce: z.string().regex(/^[A-Za-z0-9]{16,64}$/),
  expiresAt: Timestamp,
});

export const SiweVerifyRequest = z.strictObject({
  message: z.string().min(1).max(4000),
  signature: z.string().regex(/^0x[0-9a-fA-F]{2,8192}$/),
});

export const SessionResponse = z.strictObject({
  token: z.string().min(32),
  address: Address,
  expiresAt: Timestamp,
  admin: z.boolean(),
});

/** Holder benefits (SPEC 8.3): free small runs and a discount, never yield or payouts. */
export const HolderStatus = z.strictObject({
  enabled: z.boolean(),
  eligible: z.boolean(),
  /** Token units (decimal strings); balances are null when they could not be read. */
  minBalance: z.string(),
  balance: z.string().nullable(),
  balance24hAgo: z.string().nullable(),
  freeRunsPerMonth: z.int().nonnegative(),
  freeRunsLeft: z.int().nonnegative(),
  discountBps: z.int().min(0).max(10_000),
  freeRunMax: z.strictObject({ agents: z.int().min(1), rounds: z.int().min(1) }),
});

export const MeResponse = z.strictObject({
  address: Address,
  admin: z.boolean(),
  holder: HolderStatus,
});

// ---------------------------------------------------------------------------------------------
// Quotes, orders and payments

/** What to run. Crowd and round bounds come from the API config and are checked there. */
const RunRequestFields = {
  scenario: ScenarioInput,
  agents: z.int(),
  rounds: z.int(),
  mix: MixInput.nullable().optional(),
  listed: z.boolean().optional(),
};

function checkRunRequest(
  request: { scenario: ScenarioInput; agents: number; rounds: number; mix?: MixInput | null | undefined },
  ctx: z.RefinementCtx,
): void {
  const late = request.scenario.events.filter((event) => event.round > request.rounds);
  if (late.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["scenario", "events"],
      message: `events at round ${late.map((e) => e.round).join(", ")} fall after the last round (${request.rounds})`,
    });
  }
  if (request.mix) {
    const total = Object.values(request.mix).reduce<number>((sum, count) => sum + (count ?? 0), 0);
    if (total !== request.agents) {
      ctx.addIssue({ code: "custom", path: ["mix"], message: `the mix adds up to ${total}, not ${request.agents}` });
    }
  }
}

export const QuoteRequest = z.strictObject(RunRequestFields).superRefine(checkRunRequest);

export const PaymentToken = z.strictObject({ address: Address, symbol: z.string(), decimals: z.int() });

export const OrderStatus = z.enum(["quoted", "paid", "expired"]);

export const Order = z.strictObject({
  /** The PaymentRouter `orderId` (bytes32). */
  orderId: Hash32,
  status: OrderStatus,
  address: Address,
  token: PaymentToken,
  /** Token base units as a decimal string. */
  amount: z.string().regex(/^[0-9]+$/),
  priceUsd: z.number().nonnegative(),
  estimatedCostUsd: z.number().nonnegative(),
  discountBps: z.int().min(0).max(10_000),
  createdAt: Timestamp,
  expiresAt: Timestamp,
  paidAt: Timestamp.nullable(),
  runId: RunId.nullable(),
  chainId: z.int(),
  paymentRouter: Address,
  run: z.strictObject({ title: z.string(), agents: z.int(), rounds: z.int(), listed: z.boolean() }),
});

export const OrderResponse = z.strictObject({ order: Order });
export const OrderListResponse = z.strictObject({ orders: z.array(Order) });

export const PaymentCheckRequest = z.strictObject({ txHash: Hash32 });

export const PaymentCheck = z.enum(["paid", "pending", "not_found", "failed", "no_payment", "mismatch"]);

export const PaymentCheckResponse = z.strictObject({
  order: Order,
  check: PaymentCheck,
  message: z.string(),
});

// ---------------------------------------------------------------------------------------------
// Runs: creation and status

export const CreateRunRequest = z.union([
  z.strictObject({ orderId: Hash32 }),
  z.strictObject({ credit: z.literal(true), ...RunRequestFields }).superRefine(checkRunRequest),
]);

export const JobState = z.enum(["queued", "running", "complete", "failed"]);

export const JobView = z.strictObject({
  status: JobState,
  attempt: z.int().nonnegative(),
  progress: z.strictObject({ round: z.int().min(0), rounds: z.int().min(1) }).nullable(),
  queuedAt: Timestamp,
  startedAt: Timestamp.nullable(),
  finishedAt: Timestamp.nullable(),
  refund: z.enum(["due", "sent"]).nullable(),
});

export const OracleState = z.enum(["pending", "sent", "confirmed", "failed"]);

export const OracleView = z.strictObject({
  status: OracleState,
  txHash: Hash32.nullable(),
  blockNumber: z.int().nullable(),
  chainId: z.int(),
  oracleAddress: Address,
});

/** `GET /v1/runs/:runId`: the published summary once there is one, the job while it runs. */
export const RunResponse = z.strictObject({
  run: RunSummary.nullable(),
  job: JobView.nullable(),
  oracle: OracleView.nullable(),
  disclaimer: Disclaimer,
});

export const CreateRunResponse = z.strictObject({ runId: RunId, job: JobView });

// ---------------------------------------------------------------------------------------------
// Oracle ledger

export const OracleEntry = z.strictObject({
  runId: RunId,
  runIdBytes32: Hash32,
  /** `reportHash` is the run hash (docs/RUN_FORMAT.md). */
  reportHash: Hash32,
  scenarioHash: Hash32,
  /** null for unlisted runs. */
  title: z.string().nullable(),
  status: OracleState,
  txHash: Hash32.nullable(),
  blockNumber: z.int().nullable(),
  committedAt: Timestamp.nullable(),
});

export const OracleLedgerResponse = z.strictObject({
  chainId: z.int(),
  oracleAddress: Address.nullable(),
  entries: z.array(OracleEntry),
  nextCursor: z.string().nullable(),
  disclaimer: Disclaimer,
});

export const OnChainCommit = z.strictObject({
  reportHash: Hash32,
  scenarioHash: Hash32,
  resolvesAt: z.int().nonnegative(),
  committedAt: z.int().nonnegative(),
  resolvedAt: z.int().nonnegative(),
  outcomeHash: Hash32,
});

export const OracleRunResponse = z.strictObject({
  entry: OracleEntry,
  chainId: z.int(),
  oracleAddress: Address.nullable(),
  /** Read from the chain by the API when it answered; verify it yourself (docs/API.md). */
  onChain: OnChainCommit.nullable(),
  matches: z.boolean().nullable(),
  disclaimer: Disclaimer,
});

// ---------------------------------------------------------------------------------------------
// Admin (founder wallets only)

export const RefundView = z.strictObject({
  id: z.string(),
  reason: z.string(),
  runId: RunId.nullable(),
  orderId: Hash32.nullable(),
  address: Address,
  token: Address,
  /** null for a token the API does not know: `amount` is then only meaningful in base units. */
  symbol: z.string().nullable(),
  decimals: z.int().nullable(),
  amount: z.string().regex(/^[0-9]+$/),
  status: z.enum(["due", "sent"]),
  txHash: Hash32.nullable(),
  note: z.string().nullable(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
});

export const AdminOverviewResponse = z.strictObject({
  now: Timestamp,
  features: z.strictObject({ payments: z.boolean(), oracle: z.boolean(), holder: z.boolean() }),
  queue: z.strictObject({
    paused: z.boolean(),
    queued: z.int().nonnegative(),
    running: z.int().nonnegative(),
    spendTodayUsd: z.number().nonnegative(),
    dailyCapUsd: z.number().nonnegative(),
  }),
  fees: z.strictObject({
    todayUsd: z.number().nonnegative(),
    last30dUsd: z.number().nonnegative(),
    paymentsLast30d: z.int().nonnegative(),
  }),
  jobs: z.strictObject({
    completeLast7d: z.int().nonnegative(),
    partialLast7d: z.int().nonnegative(),
    failedLast7d: z.int().nonnegative(),
  }),
  failures: z.array(
    z.strictObject({
      runId: RunId,
      failedAt: Timestamp,
      reason: z.string(),
      refund: z.enum(["due", "sent"]).nullable(),
    }),
  ),
  refunds: z.array(RefundView),
  oracle: z.strictObject({
    pending: z.int().nonnegative(),
    failed: z.int().nonnegative(),
    lastError: z.string().nullable(),
  }),
});

export const RefundSentRequest = z.strictObject({ txHash: Hash32, note: z.string().max(500).optional() });
export const QueueRequest = z.strictObject({ paused: z.boolean() });

export type RunSummary = z.infer<typeof RunSummary>;
export type RunListResponse = z.infer<typeof RunListResponse>;
export type FeaturedRunResponse = z.infer<typeof FeaturedRunResponse>;
export type RunResponse = z.infer<typeof RunResponse>;
export type ArtifactResponse = z.infer<typeof ArtifactResponse>;
export type PublishResponse = z.infer<typeof PublishResponse>;
export type ErrorResponse = z.infer<typeof ErrorResponse>;
export type HealthResponse = z.infer<typeof HealthResponse>;
export type RunMeta = z.infer<typeof RunMeta>;
export type PublishMeta = z.infer<typeof PublishMeta>;
export type NonceResponse = z.infer<typeof NonceResponse>;
export type SiweVerifyRequest = z.infer<typeof SiweVerifyRequest>;
export type SessionResponse = z.infer<typeof SessionResponse>;
export type HolderStatus = z.infer<typeof HolderStatus>;
export type MeResponse = z.infer<typeof MeResponse>;
export type QuoteRequest = z.infer<typeof QuoteRequest>;
export type PaymentToken = z.infer<typeof PaymentToken>;
export type OrderStatus = z.infer<typeof OrderStatus>;
export type Order = z.infer<typeof Order>;
export type OrderResponse = z.infer<typeof OrderResponse>;
export type OrderListResponse = z.infer<typeof OrderListResponse>;
export type PaymentCheck = z.infer<typeof PaymentCheck>;
export type PaymentCheckResponse = z.infer<typeof PaymentCheckResponse>;
export type CreateRunRequest = z.infer<typeof CreateRunRequest>;
export type JobState = z.infer<typeof JobState>;
export type JobView = z.infer<typeof JobView>;
export type OracleState = z.infer<typeof OracleState>;
export type OracleView = z.infer<typeof OracleView>;
export type CreateRunResponse = z.infer<typeof CreateRunResponse>;
export type OracleEntry = z.infer<typeof OracleEntry>;
export type OracleLedgerResponse = z.infer<typeof OracleLedgerResponse>;
export type OnChainCommit = z.infer<typeof OnChainCommit>;
export type OracleRunResponse = z.infer<typeof OracleRunResponse>;
export type RefundView = z.infer<typeof RefundView>;
export type AdminOverviewResponse = z.infer<typeof AdminOverviewResponse>;
